import type { Room } from "@spieleparadies/shared";

import { usePlayerStore } from "../state/usePlayerStore";
import type { VoiceChatHandle } from "../lib/useVoiceChat";

interface VoiceChatBarProps {
  voice: VoiceChatHandle;
  room: Room | null;
  compact?: boolean;
}

export function VoiceChatBar({ voice, room, compact = false }: VoiceChatBarProps) {
  const myId = usePlayerStore((s) => s.id);

  const peerName = (id: string): string => {
    const player = room?.players.find((p) => p.id === id);
    return player?.name ?? "Spieler";
  };
  const peerColor = (id: string): string => {
    const player = room?.players.find((p) => p.id === id);
    return player?.color ?? "#888";
  };

  return (
    <div className="voice-bar">
      {!voice.enabled ? (
        <button
          type="button"
          className="btn"
          onClick={() => void voice.enable()}
          title={
            voice.available
              ? "Voice-Chat starten"
              : "Voice-Chat braucht HTTPS oder localhost"
          }
          disabled={!voice.available}
        >
          🎙️ {compact ? "" : voice.available ? "Voice-Chat starten" : "Voice nicht verfügbar"}
        </button>
      ) : (
        <>
          <button
            type="button"
            className={`btn ${voice.muted ? "btn-ghost" : "btn-primary"}`}
            onClick={voice.toggleMute}
            title={voice.muted ? "Mikro einschalten" : "Stummschalten"}
          >
            {voice.muted ? "🔇" : voice.speaking ? "🎤" : "🎙️"}{" "}
            {compact ? "" : voice.muted ? "Stumm" : "An"}
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={voice.disable}
            title="Voice-Chat beenden"
          >
            ✕ {compact ? "" : "Beenden"}
          </button>

          {/* Ich selbst */}
          <span
            className={`voice-peer ${voice.speaking ? "speaking" : ""} ${
              voice.muted ? "muted" : ""
            }`}
          >
            <span
              className="voice-dot"
              style={{
                background:
                  room?.players.find((p) => p.id === myId)?.color ?? "#7c3aed",
              }}
            />
            Ich {voice.muted ? "(stumm)" : ""}
          </span>

          {/* Mitspieler */}
          {voice.peers.map((peer) => (
            <span
              key={peer.id}
              className={`voice-peer ${peer.speaking ? "speaking" : ""}`}
            >
              <span className="voice-dot" style={{ background: peerColor(peer.id) }} />
              {peerName(peer.id)}
            </span>
          ))}
        </>
      )}

      {!voice.available && !voice.enabled && (
        <span
          className="voice-peer"
          style={{ borderColor: "rgba(251,191,36,0.5)", color: "#fde68a", fontSize: 12 }}
        >
          Voice-Chat braucht HTTPS. Auf dem Handy funktioniert er nur über
          einen sicheren Tunnel (z.&nbsp;B. ngrok) oder localhost.
        </span>
      )}

      {voice.error && (
        <span className="voice-peer" style={{ borderColor: "var(--danger)", color: "var(--danger)" }}>
          {voice.error}
        </span>
      )}
    </div>
  );
}
